import { Alert, Box, CircularProgress, Snackbar, styled } from '@mui/material';
import { bookingsAPI } from 'api/bookingsAPI';
import useBookingById from 'hooks/useBookingById';
import useBookingJobs from 'hooks/useBookingJobs';
import useDriverLocations from 'hooks/useDriverLocations';
import useFlightLookup from 'hooks/useFlightLookup';
import useOnwardBookings from 'hooks/useOnwardBookings';
import { DateTime } from 'luxon';
import { drawerWidth } from 'materialUI/jss/material-dashboard-pro-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { fireStartBookingProcess } from '../../../analytics/bookingEvents';
import {
    copyBooking,
    setCancelSuccess,
} from '../../../reducers/bookingsReducer';
import { looksLikeAirport } from './components/AirportServices';
import MapBackground from './components/MapBackground';
import OnwardMapOverlay from './components/OnwardMapOverlay';
import OnwardSuggestionBubble, {
    BUBBLE_TAIL_HEIGHT,
    BUBBLE_WIDTH,
} from './components/OnwardSuggestionBubble';
import TopBar from './components/TopBar';
import {
    BookingStep,
    BookingWizardProvider,
    RouteInfo,
    useBookingWizardContext,
} from './hooks/useBookingWizard';
import { useOnwardActions } from './hooks/useOnwardActions';
import useSupplierAnimation from './hooks/useSupplierAnimation';

const JourneyStep = React.lazy(() =>
  import('./components/JourneyStep').catch(() => ({
    default: () => <div>JourneyStep component</div>,
  })),
)
const VehicleStep = React.lazy(() =>
  import('./components/VehicleStep').catch((err) => {
    console.error('[VehicleStep lazy]', err)
    return { default: () => <div>VehicleStep component (pending)</div> }
  }),
)

const ConfirmationStep = React.lazy(() =>
  import('./components/ConfirmationStep').catch(() => ({
    default: () => <div>ConfirmationStep component (pending)</div>,
  })),
)

const PANEL_GUTTER = 16
const TOPBAR_HEIGHT = 70

const SidePanel = styled(Box)(({ theme }) => ({
  position: 'fixed',
  left: drawerWidth + PANEL_GUTTER,
  top: TOPBAR_HEIGHT + PANEL_GUTTER,
  maxHeight: `calc(var(--app-vh) - ${TOPBAR_HEIGHT + PANEL_GUTTER * 2}px)`,
  width: 552,
  backgroundColor: theme.palette.common.white,
  borderRadius: 12,
  boxShadow: theme.shadows[8],
  zIndex: 50,
  display: 'flex',
  flexDirection: 'column',

  overflow: 'hidden',

  '&&& .MuiOutlinedInput-notchedOutline': {
    borderWidth: 1,
    borderColor: theme.palette.divider,
  },
  '&&& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderWidth: 1,
    borderColor: theme.palette.grey[400],
  },
  '&&& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: 1,
    borderColor: theme.palette.brand.dark,
  },
}))

const PanelContent = styled(Box)(({ theme }) => ({
  padding: '0 24px 0 24px',
  flex: 1,
  minHeight: 0,

  overflowY: 'auto',
  overflowX: 'hidden',

  '&::-webkit-scrollbar': {
    width: 6,
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.grey[100],
    borderRadius: 8,
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.divider,
    borderRadius: 8,
    '&:hover': {
      background: theme.palette.text.disabled,
    },
  },

  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.divider} ${theme.palette.grey[100]}`,
}))

const LoadingOverlay = styled(Box)({
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 200,
})

interface RootState {
  bookings: {
    copyBooking: any
    quickQuote: boolean
    cancelSuccess: boolean
  }
  auth: {
    user: {
      firstName?: string
      lastName?: string
      email?: string
    }
  }
  accounts: {
    current: {
      name?: string
    }
  }
}

const WizardMapPageContent: React.FC = () => {
  const dispatch = useDispatch()
  const wizard = useBookingWizardContext()
  const location = useLocation()
  const history = useHistory()
  const hasCleared = useRef(false)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('new') === 'true' && !hasCleared.current) {
      hasCleared.current = true
      wizard.clearAll()

      history.replace(location.pathname)
    }
  }, [])

  const copyBookingId = useSelector(
    (state: RootState) => state.bookings?.copyBooking,
  )
  const cancelSuccess = useSelector(
    (state: RootState) => state.bookings?.cancelSuccess,
  )
  const quickQuote = useSelector(
    (state: RootState) => state.bookings?.quickQuote,
  )
  const user = useSelector((state: RootState) => state.auth?.user)
  const currentClient = useSelector((state: any) => state.clients?.client)
  const currentUser = useSelector((state: any) => state.users?.user)

  const startFiredRef = useRef(false)
  const fireStartOnce = () => {
    if (startFiredRef.current) return
    startFiredRef.current = true
    fireStartBookingProcess({
      flow: quickQuote ? 'quickquote' : 'new',
      copiedBooking: copyBookingId != null,
    })
  }
  const currentAccount = useSelector(
    (state: RootState) => state.accounts?.current,
  )

  const {
    booking: currentBooking,
    isLoading: bookingLoading,
    isFetching: bookingFetching,
  } = useBookingById(copyBookingId)
  const {
    jobs: bookingJobs,
    isLoading: jobsLoading,
    isFetching: jobsFetching,
  } = useBookingJobs(copyBookingId)

  const [copyApplied, setCopyApplied] = useState(false)
  const [needsCopy, setNeedsCopy] = useState(copyBookingId != null)
  useEffect(() => {
    if (copyBookingId != null) setNeedsCopy(true)
  }, [copyBookingId])

  const isApplyingCopy = needsCopy && !copyApplied
  const isLoadingCopy =
    isApplyingCopy &&
    (bookingLoading || bookingFetching || jobsLoading || jobsFetching)

  const onwardPickup = wizard.journeyData?.outbound?.pickup
  const onwardDropoff = wizard.journeyData?.outbound?.dropOff
  const onwardWhen = wizard.journeyData?.outbound?.when
  const pickupIsAirport = looksLikeAirport(onwardPickup)
  const dropoffIsAirport = looksLikeAirport(onwardDropoff)
  const journeyTypeName = wizard.journeyData?.journeyTypeName
  const isReturnJourney =
    journeyTypeName === 'Return' || journeyTypeName === 'WaitAndReturn'

  const outboundFlightNumber =
    wizard.journeyData?.outbound?.flightDetails?.flightNumber
  const inboundFlightNumber =
    wizard.journeyData?.inbound?.flightDetails?.flightNumber

  const shouldOfferOnward =
    (dropoffIsAirport && !!outboundFlightNumber?.trim()) ||
    (pickupIsAirport && isReturnJourney && !!inboundFlightNumber?.trim())

  const onwardFlightNumber = dropoffIsAirport
    ? outboundFlightNumber
    : inboundFlightNumber

  const toLocalDateStr = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate(),
    ).padStart(2, '0')}`
  const onwardDateStr = onwardWhen
    ? toLocalDateStr(new Date(onwardWhen))
    : undefined

  const { flight: onwardFlight } = useFlightLookup({
    flightNumber: onwardFlightNumber,
    date: onwardDateStr,
    isArrival: false, // always a departing flight - we want where they land
    enabled:
      shouldOfferOnward && !!onwardFlightNumber?.trim() && !!onwardDateStr,
  })

  const mapFlightArc = wizard.mapFlightArc

  // const isImmediateJourney =
  //   journeyTypeName === 'OneWay' && !!wizard.journeyData?.isImmediateJourney
  const pickupForDrivers = wizard.mapLocations.pickup
  const driverLocations = useDriverLocations({
    lat: pickupForDrivers?.latitude,
    lng: pickupForDrivers?.longitude,
    enabled: false,
  })
  const [mapBounds, setMapBounds] = useState<{
    north: number
    south: number
    east: number
    west: number
  } | null>(null)
  const handleBoundsChange = useCallback(
    (b: { north: number; south: number; east: number; west: number }) => {
      setMapBounds((prev) =>
        prev &&
        prev.north === b.north &&
        prev.south === b.south &&
        prev.east === b.east &&
        prev.west === b.west
          ? prev
          : b,
      )
    },
    [],
  )
  const lastRouteInfoRef = useRef<RouteInfo>()
  const handleRouteInfoChange = useCallback(
    (info: RouteInfo) => {
      const prev = lastRouteInfoRef.current
      if (
        prev &&
        prev.estimatedJourneyDistance === info.estimatedJourneyDistance &&
        prev.estimatedJourneyTime === info.estimatedJourneyTime
      ) {
        return
      }
      lastRouteInfoRef.current = info
      wizard.setRouteInfo(info)
    },
    [wizard.setRouteInfo],
  )

  const driverMarkers = useMemo(() => {
    const inBounds = (lat: number, lng: number) => {
      if (!mapBounds) return true
      const { north, south, east, west } = mapBounds
      if (lat < south || lat > north) return false
      if (west <= east) return lng >= west && lng <= east

      return lng >= west || lng <= east
    }
    return driverLocations
      .filter((d) => inBounds(d.latitude, d.longitude))
      .map((d, i) => ({
        key: `driver-${i}`,
        position: { latitude: d.latitude, longitude: d.longitude },
        kind: 'dot' as const,
        tooltip: `Driver · ${d.status}${
          typeof d.eta === 'number' ? ` · ${d.eta}m ETA` : ''
        }`,
      }))
  }, [driverLocations, mapBounds])

  const SUPPLIER_ANIMATION_ENABLED = false
  const isAnimatingSuppliers =
    SUPPLIER_ANIMATION_ENABLED &&
    wizard.currentStep === 'vehicle' &&
    wizard.isLoadingQuotes
  const supplierMarkers = useSupplierAnimation({
    pickup: wizard.mapLocations.pickup,
    isActive: isAnimatingSuppliers,
    bounds: mapBounds,
  })

  const allMapMarkers = useMemo(
    () => [...driverMarkers, ...supplierMarkers],
    [driverMarkers, supplierMarkers],
  )

  const showOnwardOverlay =
    !!wizard.confirmedBookingId && wizard.onwardSuggestions.length > 0

  const onwardArrivalAirport = useMemo(() => {
    if (!showOnwardOverlay) return undefined
    const a = wizard.onwardSuggestions[0]?.arrivalAirport
    if (a && a.latitude != null && a.longitude != null) {
      return { latitude: a.latitude, longitude: a.longitude }
    }
    const to = mapFlightArc?.to
    if (to && to.latitude != null && to.longitude != null) {
      return { latitude: to.latitude, longitude: to.longitude }
    }
    return undefined
  }, [showOnwardOverlay, wizard.onwardSuggestions, mapFlightArc?.to])

  const onwardFitToPoints = useMemo(() => {
    if (!showOnwardOverlay) return undefined
    if (!onwardArrivalAirport) return undefined
    const pts: Array<{ latitude: number; longitude: number }> = [
      onwardArrivalAirport,
    ]
    for (const s of wizard.onwardSuggestions) {
      pts.push({
        latitude: s.destination.latitude,
        longitude: s.destination.longitude,
      })
    }
    return pts
  }, [showOnwardOverlay, wizard.onwardSuggestions, onwardArrivalAirport])

  const onwardViewportPadding = useMemo(() => {
    if (!showOnwardOverlay) return undefined
    const bubbleHeightMax = 220
    const margin = 24
    const vert = bubbleHeightMax + BUBBLE_TAIL_HEIGHT + margin
    const horiz = BUBBLE_WIDTH + BUBBLE_TAIL_HEIGHT + margin
    return {
      top: vert,
      right: horiz,
      bottom: vert,
      left: 592 + horiz,
    }
  }, [showOnwardOverlay])

  const onwardExtraRoutes = useMemo(() => {
    if (!showOnwardOverlay) return undefined
    if (!onwardArrivalAirport) return undefined
    return wizard.onwardSuggestions.map((s, i) => ({
      key: `onward-route-${i}`,
      origin: onwardArrivalAirport,
      destination: {
        latitude: s.destination.latitude,
        longitude: s.destination.longitude,
      },
    }))
  }, [showOnwardOverlay, wizard.onwardSuggestions, onwardArrivalAirport])

  const { selectSuggestion: selectOnwardSuggestion } = useOnwardActions()

  const onwardHtmlOverlays = useMemo(() => {
    if (!showOnwardOverlay) return undefined
    return wizard.onwardSuggestions.map((s, i) => ({
      key: `onward-bubble-${i}`,
      position: {
        latitude: s.destination.latitude,
        longitude: s.destination.longitude,
      },
      size: { width: BUBBLE_WIDTH, height: 200 },
      tailHeight: BUBBLE_TAIL_HEIGHT,
      preferAwayFrom: onwardArrivalAirport,
      render: ({
        tailSide,
        tailAttach,
      }: {
        tailSide: 'top' | 'right' | 'bottom' | 'left'
        tailAttach: number
      }) => (
        <OnwardSuggestionBubble
          suggestion={s}
          onSelect={selectOnwardSuggestion}
          tailSide={tailSide}
          tailAttach={tailAttach}
        />
      ),
    }))
  }, [
    showOnwardOverlay,
    wizard.onwardSuggestions,
    selectOnwardSuggestion,
    onwardArrivalAirport,
  ])

  const effectiveMapPickup = showOnwardOverlay
    ? undefined
    : wizard.mapLocations.pickup
  const effectiveMapDropoff = showOnwardOverlay
    ? undefined
    : wizard.mapLocations.dropoff
  const effectiveMapStops = showOnwardOverlay ? [] : wizard.mapLocations.stops

  useEffect(() => {
    console.log('[FlightArc] mapFlightArc (from wizard):', mapFlightArc)
  }, [mapFlightArc])

  const onwardBookings = useOnwardBookings({
    clientId: wizard.accountData?.clientId,
    leadPassengerId: wizard.accountData?.mainPassenger?.userId,
    flightNumber: onwardFlightNumber,
    pickupDateTime: onwardWhen ? new Date(onwardWhen).toISOString() : undefined,
    arrivalAirport: onwardFlight?.arrival ?? undefined,
    accountNumber: wizard.accountData?.accountNumber,
    numberOfPassengers: wizard.accountData?.numberOfPassengers,
    passengers: wizard.accountData?.passengers,
    mainPassenger: wizard.accountData?.mainPassenger,
    currentStep: wizard.currentStep,
  })

  useEffect(() => {
    console.log('[Onward] flightNumber:', onwardFlightNumber)
    console.log('[Onward] dateStr:', onwardDateStr)
    console.log('[Onward] dropoffIsAirport:', dropoffIsAirport)
    console.log('[Onward] flight resolved:', onwardFlight)
    console.log('[Onward] arrivalAirport:', onwardFlight?.arrival)
    console.log('[Onward] clientId:', wizard.accountData?.clientId)
    console.log('[Onward] accountNumber:', wizard.accountData?.accountNumber)
    console.log('[Onward] currentStep:', wizard.currentStep)
    console.log('[Onward] suggestions:', onwardBookings.suggestions)
    console.log(
      '[Onward] isLoadingLocations:',
      onwardBookings.isLoadingLocations,
    )
    console.log('[Onward] isLoadingQuotes:', onwardBookings.isLoadingQuotes)
  }, [
    onwardFlightNumber,
    onwardDateStr,
    onwardFlight,
    wizard.currentStep,
    onwardBookings.suggestions,
    onwardBookings.isLoadingLocations,
    onwardBookings.isLoadingQuotes,
  ])

  useEffect(() => {
    if (onwardBookings.suggestions.length > 0) {
      wizard.setOnwardSuggestions(onwardBookings.suggestions)
    }
  }, [onwardBookings.suggestions])

  const copyHydratedRef = useRef(false)
  useEffect(() => {
    if (copyHydratedRef.current) return

    if (
      !isApplyingCopy ||
      isLoadingCopy ||
      !currentBooking ||
      !currentClient?.id ||
      !currentUser?.userId
    )
      return

    copyHydratedRef.current = true

    const hydrate = async () => {
      const copied = currentBooking

      let outboundBooking = copied
      let outboundJobs = bookingJobs
      let inboundBooking: any = null
      let inboundJobs: any = null

      if (copied.journeyTypeName === 'Return') {
        const relatedId = copied.parentBookingId ?? copied.associatedBookings
        if (relatedId) {
          const related = await Promise.all([
            bookingsAPI.getBooking(relatedId),
            bookingsAPI.getBookingJobs(relatedId),
          ]).catch(() => null)
          if (related) {
            const [relatedBooking, relatedJobs] = related
            if (copied.parentBookingId) {
              outboundBooking = relatedBooking
              outboundJobs = relatedJobs
              inboundBooking = copied
              inboundJobs = bookingJobs
            } else {
              inboundBooking = relatedBooking
              inboundJobs = relatedJobs
            }
          }
        }
      }

      const mapLeg = (leg: any, legJobs: any) => {
        const when = leg.when
          ? DateTime.fromISO(leg.when)
              .setZone(leg.timeZone)
              .toISO({ includeOffset: false })
          : undefined
        const services =
          Array.isArray(legJobs?.value) &&
          (legJobs.value[0]?.services?.length ?? 0) > 0
            ? 'MeetAndGreet'
            : undefined
        const legHasDropOff = !!leg.dropOff?.formattedAddress
        return {
          ...(when && { when }),
          pickup: leg.pickup,
          ...(services && { services }),
          ...(legHasDropOff && { dropOff: leg.dropOff }),
          timeZone: leg.timeZone,
          specialInstructions: leg.specialInstructions,
        }
      }

      const booking = outboundBooking
      const hasDropOff = !!booking.dropOff?.formattedAddress

      const journeyData: any = {
        journeyTypeName: booking.journeyTypeName,
        isImmediateJourney: booking.isImmediateJourney,
        ...(booking.directedJourneyTime && {
          directedJourneyTime: booking.directedJourneyTime,
        }),
        ...(booking.waitBeforeReturnTime != null && {
          waitHours: String(Math.floor(booking.waitBeforeReturnTime / 60)),
          waitMinutes: String(booking.waitBeforeReturnTime % 60).padStart(
            2,
            '0',
          ),
        }),
        outbound: mapLeg(outboundBooking, outboundJobs),
        ...(inboundBooking && {
          inbound: mapLeg(inboundBooking, inboundJobs),
        }),
        additionalStops: booking.additionalStops,
      }

      const leadPassenger = Array.isArray(booking.passengers)
        ? booking.passengers.find((p: any) => p.isLeadPassenger === true)
        : undefined
      const otherPassengers = Array.isArray(booking.passengers)
        ? booking.passengers
            .filter((p: any) => !p.isLeadPassenger)
            .map((p: any) => ({
              userId: p.userId,
              name: p.name,
              isLeadPassenger: p.isLeadPassenger,
              contactNumber: p.contactNumber,
              email: p.email,
            }))
        : []

      const leadHasUserId = leadPassenger?.userId != null
      const leadIsCurrentUser =
        leadHasUserId &&
        currentUser?.userId != null &&
        leadPassenger.userId === currentUser.userId

      const bookingClientId = booking.client?.clientId
      const isDifferentAccount =
        bookingClientId != null &&
        currentClient?.id != null &&
        bookingClientId !== currentClient.id

      const bookingMode = isDifferentAccount
        ? 'different-account'
        : leadIsCurrentUser
        ? 'for-me'
        : 'for-someone'

      const onBehalfOf =
        !leadIsCurrentUser && leadHasUserId
          ? {
              id: leadPassenger.userId,
              firstName: leadPassenger.name,
              lastName: '',
              email: leadPassenger.email,
            }
          : null

      const bookingReferenceValues = Array.isArray(
        booking.bookingReferenceValues,
      )
        ? booking.bookingReferenceValues.map((r: any) => ({
            bookingReferenceId: r.bookingReferenceId,
            value: r.value,
          }))
        : []

      const accountData: any = {
        bookingMode,
        clientId: bookingClientId,

        accountNumber: booking.client?.accountNumber,
        alias: booking.client?.alias,
        companyName: booking.client?.companyName,
        numberOfPassengers: booking.numberOfPassengers,
        onBehalfOf,
        passengers: otherPassengers,
        bookingReferenceValues,
      }
      if (bookingMode !== 'for-me' && leadPassenger) {
        accountData.mainPassenger = {
          isLeadPassenger: true,
          userId: leadPassenger.userId,
          name: leadPassenger.name,
          email: leadPassenger.email,
          contactNumber: leadPassenger.contactNumber,
        }
      }

      wizard.clearAll()
      wizard.setJourneyData(journeyData)
      wizard.setAccountData(accountData)
      wizard.setMapLocations({
        pickup: booking.pickup,
        dropoff: hasDropOff ? booking.dropOff : undefined,
        stops: booking.additionalStops,
      })

      setCopyApplied(true)

      dispatch(copyBooking(null))
    }

    void hydrate()
  }, [
    isApplyingCopy,
    isLoadingCopy,
    currentBooking,
    bookingJobs,
    currentClient?.id,
    currentUser?.userId,
    wizard,
    dispatch,
  ])

  useEffect(() => {
    if (wizard.journeyData) {
      const jd = wizard.journeyData as any
      const pickup = jd?.outbound?.pickup ?? jd?.pickup
      const dropoff = jd?.outbound?.dropOff ?? jd?.dropoff
      const stops = jd?.additionalStops ?? jd?.stops
      if (pickup || dropoff) {
        wizard.setMapLocations({ pickup, dropoff, stops })
      }
    }
  }, [])

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  const handleCloseCancelSnackbar = () => {
    dispatch(setCancelSuccess(false))
  }

  const userName =
    user && (user.firstName || user.lastName)
      ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
      : user?.email || 'User'

  const accountName = currentAccount?.name || 'Account'

  const renderStepContent = () => {
    if (isApplyingCopy) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )
    }
    switch (wizard.currentStep) {
      case 'journey':
        return (
          <React.Suspense fallback={<CircularProgress />}>
            <JourneyStep />
          </React.Suspense>
        )
      case 'vehicle':
        return (
          <React.Suspense fallback={<CircularProgress />}>
            <VehicleStep />
          </React.Suspense>
        )
      case 'confirmation':
        return (
          <React.Suspense fallback={<CircularProgress />}>
            <ConfirmationStep />
          </React.Suspense>
        )
      default:
        return null
    }
  }

  return (
    <>
      <MapBackground
        pickup={effectiveMapPickup}
        dropoff={effectiveMapDropoff}
        stops={effectiveMapStops}
        onRouteInfoChange={handleRouteInfoChange}
        flightArc={mapFlightArc}
        flightArcFlyoverKey={wizard.flightArcFlyoverKey}
        markers={allMapMarkers}
        allowFitBounds={!isAnimatingSuppliers}
        onBoundsChange={handleBoundsChange}
        showRouteMarkers={!showOnwardOverlay}
        extraRoutes={onwardExtraRoutes}
        htmlOverlays={onwardHtmlOverlays}
        fitToPoints={onwardFitToPoints}
        viewportPadding={onwardViewportPadding}
      />

      <TopBar
        currentStep={wizard.currentStep}
        onStepClick={(step: BookingStep) => {
          const stepOrder: BookingStep[] = [
            'journey',
            'vehicle',
            'confirmation',
          ]
          const currentIndex = stepOrder.indexOf(wizard.currentStep)
          const targetIndex = stepOrder.indexOf(step)

          if (targetIndex <= currentIndex) {
            wizard.setStep(step)
          }
        }}
        userName={userName}
        accountName={accountName}
      />

      <SidePanel>
        <PanelContent
          onFocusCapture={fireStartOnce}
          onChangeCapture={fireStartOnce}
        >
          <Box sx={{ pt: 3 }}>{renderStepContent()}</Box>
        </PanelContent>
      </SidePanel>

      <OnwardMapOverlay />

      {isLoadingCopy && (
        <LoadingOverlay>
          <CircularProgress />
        </LoadingOverlay>
      )}

      <Snackbar
        open={cancelSuccess}
        autoHideDuration={6000}
        onClose={handleCloseCancelSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseCancelSnackbar}
          severity="success"
          sx={{ width: '100%' }}
        >
          Booking cancelled successfully
        </Alert>
      </Snackbar>
    </>
  )
}

const WizardMapPage: React.FC = () => {
  const quickQuote = useSelector(
    (state: RootState) => state.bookings?.quickQuote,
  )
  const copyBookingId = useSelector(
    (state: RootState) => state.bookings?.copyBooking,
  )

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <BookingWizardProvider
        initialStep="journey"
        isQuickQuote={quickQuote || false}
        isCopyBooking={copyBookingId != null}
      >
        <WizardMapPageContent />
      </BookingWizardProvider>
    </Box>
  )
}

export default WizardMapPage
